import { LocationStrategy } from '@angular/common';
import { Directive, ElementRef, HostListener, Input, Optional, Self, inject } from '@angular/core';
import { Router, RouterLink, UrlTree } from '@angular/router';

type MiddleClickCommands = string | readonly unknown[] | UrlTree;

@Directive({
	selector: '[middleClick]',
	standalone: true
})
export class MiddleClickDirective {
	@Input('middleClick') commands?: MiddleClickCommands | '';

	private readonly router = inject(Router);
	private readonly locationStrategy = inject(LocationStrategy);
	private readonly el = inject(ElementRef<HTMLElement>);

	constructor(@Optional() @Self() private routerLink: RouterLink) { }

	private getUrl(): string | null {
		const commands = this.commands;
		if (commands) {
			const urlTree =
				commands instanceof UrlTree
					? commands
					: this.router.createUrlTree(Array.isArray(commands) ? [...commands] : [commands]);

			return this.toAbsoluteUrl(this.router.serializeUrl(urlTree));
		}

		const href = this.routerLink?.href ?? this.el.nativeElement.getAttribute('href');
		if (href) {
			return this.toAbsoluteUrl(href);
		}

		if (this.routerLink?.urlTree) {
			return this.toAbsoluteUrl(this.router.serializeUrl(this.routerLink.urlTree));
		}

		return null;
	}

	private toAbsoluteUrl(url: string): string {
		const externalUrl = this.locationStrategy.prepareExternalUrl(url);
		return new URL(externalUrl, window.location.origin).href;
	}

	private openInNewTab(event: MouseEvent, url: string): void {
		event.preventDefault();
		event.stopPropagation();
		window.open(url, '_blank', 'noopener,noreferrer');
	}

	@HostListener('auxclick', ['$event'])
	onMiddleClick(event: MouseEvent): void {
		if (event.button !== 1) return;
		const url = this.getUrl();
		if (!url) return;
		this.openInNewTab(event, url);
	}

	@HostListener('click', ['$event'])
	onClick(event: MouseEvent): void {
		if (!event.ctrlKey && !event.metaKey) return;
		const url = this.getUrl();
		if (!url) return;
		this.openInNewTab(event, url);
	}
}